<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  contactId: { type: [String, Number], default: null },
});

const { t, locale } = useI18n();

const ANALYSIS_TYPES = ['closer', 'suporte', 'objecoes', 'proximos'];
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 10;

const recordings = ref([]);
const isLoading = ref(false);
const hasError = ref(false);

// Per-card UI state, keyed by `${type}-${id}`.
const playingKeys = reactive({});
const expandedTranscripts = reactive({});
const videoRefs = reactive({});
const transcribingKeys = reactive({});
const generatingAtaKeys = reactive({});
const generatingAnalysisKeys = reactive({});

const dateLocale = computed(() =>
  locale.value.startsWith('pt') ? ptBR : enUS
);

const isEmpty = computed(
  () => !isLoading.value && !hasError.value && recordings.value.length === 0
);

const cardKey = recording => `${recording.type}-${recording.id}`;

const fetchRecordings = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await ContactAPI.getRecordings(props.contactId);
    recordings.value = data?.recordings || [];
  } catch (error) {
    hasError.value = true;
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Silent refresh used while polling for on-demand results. Keeps the list
// and per-card UI state intact instead of showing the full-page spinner.
const refreshRecordings = async () => {
  if (!props.contactId) return;
  try {
    const { data } = await ContactAPI.getRecordings(props.contactId);
    recordings.value = data?.recordings || [];
  } catch (error) {
    // Ignore transient poll failures; the next attempt (or the user) can retry.
  }
};

const findRecording = (type, id) =>
  recordings.value.find(
    recording => recording.type === type && recording.id === id
  );

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Polls `getRecordings` every ~4s until `predicate` matches the refreshed
// card or the attempt budget is exhausted.
const pollUntil = async (recording, predicate) => {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(POLL_INTERVAL_MS);
    // eslint-disable-next-line no-await-in-loop
    await refreshRecordings();
    const updated = findRecording(recording.type, recording.id);
    if (updated && predicate(updated)) return true;
  }
  return false;
};

const isTranscribing = recording => !!transcribingKeys[cardKey(recording)];
const isGeneratingAta = recording => !!generatingAtaKeys[cardKey(recording)];
const isAnalysisGenerating = (recording, type) =>
  !!generatingAnalysisKeys[cardKey(recording)]?.[type];
const isAnyAnalysisGenerating = recording =>
  ANALYSIS_TYPES.some(type => isAnalysisGenerating(recording, type));

const setAnalysisGenerating = (recording, type, value) => {
  const key = cardKey(recording);
  generatingAnalysisKeys[key] = {
    ...(generatingAnalysisKeys[key] || {}),
    [type]: value,
  };
};

const handleTranscribe = async recording => {
  const key = cardKey(recording);
  if (transcribingKeys[key]) return;
  transcribingKeys[key] = true;
  try {
    await ContactAPI.transcribeRecording(
      props.contactId,
      recording.type,
      recording.id
    );
    await pollUntil(recording, updated => !!updated.transcript);
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.TRANSCRIBE_ERROR'));
  } finally {
    transcribingKeys[key] = false;
  }
};

const handleGenerateAta = async recording => {
  const key = cardKey(recording);
  if (generatingAtaKeys[key]) return;
  generatingAtaKeys[key] = true;
  try {
    await ContactAPI.generateRecordingAta(
      props.contactId,
      recording.type,
      recording.id
    );
    await pollUntil(recording, updated => !!updated.ata);
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.ATA_ERROR'));
  } finally {
    generatingAtaKeys[key] = false;
  }
};

const handleAnalyze = async (recording, analysisType) => {
  if (isAnalysisGenerating(recording, analysisType)) return;
  setAnalysisGenerating(recording, analysisType, true);
  try {
    await ContactAPI.analyzeRecording(
      props.contactId,
      recording.type,
      recording.id,
      analysisType
    );
    await pollUntil(recording, updated => !!updated.analyses?.[analysisType]);
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.ANALYZE_ERROR'));
  } finally {
    setAnalysisGenerating(recording, analysisType, false);
  }
};

const analysisOptionLabel = (recording, type) => {
  const label = t(
    `CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.${type.toUpperCase()}`
  );
  if (recording.analyses?.[type]) {
    return `${label} ${t(
      'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.GENERATED_SUFFIX'
    )}`;
  }
  return label;
};

const analysisOptions = recording =>
  ANALYSIS_TYPES.map(type => ({
    value: type,
    label: analysisOptionLabel(recording, type),
  }));

const hasAnyAnalysis = recording =>
  ANALYSIS_TYPES.some(type => !!recording.analyses?.[type]);

const analysisTypeBadge = analysis => {
  const tipo = (analysis?.tipo || '').toLowerCase();
  if (tipo === 'venda') {
    return t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.CLOSER');
  }
  if (tipo === 'suporte') {
    return t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.SUPORTE');
  }
  return analysis?.tipo || '';
};

const sentimentClass = sentimento => {
  const value = (sentimento || '').toLowerCase();
  if (value.includes('posit')) return 'bg-n-teal-3 text-n-teal-11';
  if (value.includes('negat')) return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

const formatRelativeDate = isoDate => {
  if (!isoDate) return '';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, {
    addSuffix: true,
    locale: dateLocale.value,
  });
};

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return '';
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// "Processando" (spinner) SÓ para gravação ativa/finalizando. `pending` = a
// gravação nunca começou (reunião criada mas não realizada) → indisponível, não
// fica girando pra sempre. `failed` idem.
const ACTIVE_RECORDING_STATUSES = ['recording', 'processing'];

const isProcessing = recording =>
  ACTIVE_RECORDING_STATUSES.includes(
    (recording.recording_status || '').toLowerCase()
  );

const statusLabelKey = recording =>
  isProcessing(recording)
    ? 'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.STATUS.PROCESSING'
    : 'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.STATUS.UNAVAILABLE';

const setVideoRef = (el, key) => {
  if (el) videoRefs[key] = el;
};

const handleCoverClick = recording => {
  if (recording.type !== 'meeting' || !recording.recording_url) return;
  const key = cardKey(recording);
  const videoEl = videoRefs[key];
  if (videoEl) videoEl.play();
};

const markPlaying = key => {
  playingKeys[key] = true;
};

const markPaused = key => {
  playingKeys[key] = false;
};

const toggleTranscript = key => {
  expandedTranscripts[key] = !expandedTranscripts[key];
};

onMounted(fetchRecordings);
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-4">
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <p
      v-else-if="hasError"
      class="px-2 py-10 text-sm leading-6 text-center text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ERROR') }}
    </p>

    <p
      v-else-if="isEmpty"
      class="px-2 py-10 text-sm leading-6 text-center text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.EMPTY') }}
    </p>

    <template v-else>
      <CardLayout
        v-for="recording in recordings"
        :key="cardKey(recording)"
        layout="col"
        class="[&>div:first-child]:gap-4"
      >
        <!-- Header -->
        <div class="flex items-center justify-between w-full gap-2">
          <div class="flex items-center min-w-0 gap-2">
            <span
              class="flex items-center justify-center rounded-lg size-8 shrink-0"
              :class="
                recording.type === 'meeting'
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-teal-3 text-n-teal-11'
              "
            >
              <span
                :class="
                  recording.type === 'meeting'
                    ? 'i-lucide-video'
                    : 'i-lucide-phone'
                "
                class="size-4"
              />
            </span>
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ recording.title }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatRelativeDate(recording.created_at) }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span
              v-if="recording.type === 'call' && recording.direction"
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              <span
                :class="
                  recording.direction === 'outbound'
                    ? 'i-lucide-arrow-up-right'
                    : 'i-lucide-arrow-down-left'
                "
                class="size-3"
              />
              {{
                t(
                  `CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.DIRECTION.${recording.direction.toUpperCase()}`
                )
              }}
            </span>
            <span
              v-if="recording.duration_seconds !== null"
              class="px-2 py-0.5 text-xs rounded-md tabular-nums bg-n-alpha-2 text-n-slate-11"
            >
              {{ formatDuration(recording.duration_seconds) }}
            </span>
          </div>
        </div>

        <!-- Player -->
        <div
          v-if="recording.type === 'meeting'"
          class="relative w-full overflow-hidden shadow-inner rounded-xl aspect-video bg-slate-900"
        >
          <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
          <video
            v-if="recording.recording_url"
            :ref="el => setVideoRef(el, cardKey(recording))"
            class="absolute inset-0 object-cover w-full h-full"
            controls
            :src="recording.recording_url"
            @play="markPlaying(cardKey(recording))"
            @pause="markPaused(cardKey(recording))"
            @ended="markPaused(cardKey(recording))"
          />
          <button
            v-if="recording.recording_url && !playingKeys[cardKey(recording)]"
            type="button"
            class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white transition-opacity bg-gradient-to-br from-n-brand via-indigo-700 to-slate-900"
            @click="handleCoverClick(recording)"
          >
            <span
              class="flex items-center justify-center transition-colors rounded-full size-14 bg-white/20 hover:bg-white/30 backdrop-blur"
            >
              <span class="text-2xl i-lucide-play" />
            </span>
            <span
              class="px-4 text-sm font-medium text-center truncate max-w-full"
            >
              {{ recording.title }}
            </span>
          </button>
          <div
            v-if="!recording.recording_url"
            class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white bg-gradient-to-br from-n-brand via-indigo-700 to-slate-900"
          >
            <span
              :class="
                isProcessing(recording)
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-video-off'
              "
              class="text-2xl"
            />
            <span class="text-sm font-medium">
              {{ t(statusLabelKey(recording)) }}
            </span>
          </div>
        </div>

        <div v-else class="flex flex-col w-full gap-3">
          <div
            class="flex flex-col items-center justify-center w-full gap-2 p-6 text-white rounded-xl bg-gradient-to-br from-n-teal-9 via-n-brand to-slate-900"
          >
            <span
              class="flex items-center justify-center rounded-full size-12 bg-white/20"
            >
              <span class="text-xl i-lucide-phone-call" />
            </span>
            <span class="text-sm font-medium text-center truncate max-w-full">
              {{ recording.title }}
            </span>
            <span
              v-if="recording.duration_seconds !== null"
              class="text-xs tabular-nums text-white/80"
            >
              {{ formatDuration(recording.duration_seconds) }}
            </span>
          </div>
          <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
          <audio
            v-if="recording.recording_url"
            controls
            class="w-full"
            :src="recording.recording_url"
          />
          <p v-else class="text-sm text-center text-n-slate-11">
            {{ t(statusLabelKey(recording)) }}
          </p>
        </div>

        <!-- On-demand actions -->
        <div class="flex flex-wrap items-center w-full gap-2">
          <Button
            v-if="!recording.transcript"
            :label="
              isTranscribing(recording)
                ? t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.TRANSCRIBING')
                : t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.TRANSCRIBE')
            "
            icon="i-lucide-captions"
            size="sm"
            variant="faded"
            color="slate"
            :is-loading="isTranscribing(recording)"
            :disabled="isTranscribing(recording)"
            @click="handleTranscribe(recording)"
          />

          <Button
            :label="
              isGeneratingAta(recording)
                ? t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.GENERATING_ATA')
                : recording.ata
                  ? t(
                      'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.REGENERATE_ATA'
                    )
                  : t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.GENERATE_ATA')
            "
            icon="i-lucide-notebook-pen"
            size="sm"
            variant="faded"
            color="slate"
            :is-loading="isGeneratingAta(recording)"
            :disabled="isGeneratingAta(recording)"
            @click="handleGenerateAta(recording)"
          />

          <div
            class="inline-flex"
            :class="{
              'opacity-60 pointer-events-none':
                isAnyAnalysisGenerating(recording),
            }"
          >
            <SelectMenu
              model-value=""
              :label="
                isAnyAnalysisGenerating(recording)
                  ? t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.ANALYZING')
                  : t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ACTIONS.ANALYZE')
              "
              :options="analysisOptions(recording)"
              sub-menu-position="bottom"
              @update:model-value="type => handleAnalyze(recording, type)"
            />
          </div>
        </div>

        <!-- Ata da reunião -->
        <div
          v-if="recording.ata"
          class="flex flex-col w-full gap-3 p-4 border rounded-xl border-n-container bg-n-alpha-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-clipboard-list size-4 text-n-slate-11" />
            <span
              class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
            >
              {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.TITLE') }}
            </span>
          </div>

          <div v-if="recording.ata.resumo" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.SUMMARY') }}
            </span>
            <p class="text-sm leading-6 text-n-slate-12">
              {{ recording.ata.resumo }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div
              v-if="recording.ata.temas?.length"
              class="flex flex-col gap-1.5"
            >
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.THEMES') }}
              </span>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(item, index) in recording.ata.temas"
                  :key="index"
                  class="flex items-start gap-1.5 text-sm text-n-slate-12"
                >
                  <span
                    class="mt-0.5 shrink-0 i-lucide-tag size-3.5 text-n-slate-10"
                  />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
            <div
              v-if="recording.ata.decisoes?.length"
              class="flex flex-col gap-1.5"
            >
              <span class="text-xs font-medium text-n-teal-11">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.DECISIONS') }}
              </span>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(item, index) in recording.ata.decisoes"
                  :key="index"
                  class="flex items-start gap-1.5 text-sm text-n-slate-12"
                >
                  <span
                    class="mt-0.5 shrink-0 i-lucide-gavel size-3.5 text-n-teal-10"
                  />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
            <div
              v-if="recording.ata.combinados?.length"
              class="flex flex-col gap-1.5"
            >
              <span class="text-xs font-medium text-n-brand">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.AGREEMENTS') }}
              </span>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(item, index) in recording.ata.combinados"
                  :key="index"
                  class="flex items-start gap-1.5 text-sm text-n-slate-12"
                >
                  <span
                    class="mt-0.5 shrink-0 i-lucide-handshake size-3.5 text-n-brand"
                  />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
            <div
              v-if="recording.ata.proximos_passos?.length"
              class="flex flex-col gap-1.5"
            >
              <span class="text-xs font-medium text-n-amber-11">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.NEXT_STEPS') }}
              </span>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(item, index) in recording.ata.proximos_passos"
                  :key="index"
                  class="flex items-start gap-1.5 text-sm text-n-slate-12"
                >
                  <span
                    class="mt-0.5 shrink-0 i-lucide-arrow-right-circle size-3.5 text-n-amber-10"
                  />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Analyses (per type, on-demand) -->
        <template v-if="hasAnyAnalysis(recording)">
          <template
            v-for="type in ANALYSIS_TYPES"
            :key="`${cardKey(recording)}-${type}`"
          >
            <div
              v-if="recording.analyses?.[type]"
              class="flex flex-col w-full gap-3 p-4 border rounded-xl border-n-container bg-n-alpha-1"
            >
              <!-- Closer / Suporte -->
              <template v-if="type === 'closer' || type === 'suporte'">
                <div class="flex items-center justify-between gap-2">
                  <span
                    class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
                  >
                    {{
                      t(
                        `CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.${type.toUpperCase()}`
                      )
                    }}
                  </span>
                  <div class="flex items-center gap-1.5">
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ analysisTypeBadge(recording.analyses[type]) }}
                    </span>
                    <span
                      v-if="recording.analyses[type].sentimento"
                      class="px-2 py-0.5 text-xs font-medium rounded-md"
                      :class="
                        sentimentClass(recording.analyses[type].sentimento)
                      "
                    >
                      {{ recording.analyses[type].sentimento }}
                    </span>
                    <span
                      v-if="
                        recording.analyses[type].nota !== undefined &&
                        recording.analyses[type].nota !== null
                      "
                      class="px-2 py-0.5 text-xs font-semibold rounded-md tabular-nums bg-n-brand/10 text-n-brand"
                    >
                      {{
                        t(
                          'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.SCORE_LABEL',
                          {
                            score: recording.analyses[type].nota,
                          }
                        )
                      }}
                    </span>
                  </div>
                </div>

                <p class="text-sm leading-6 text-n-slate-12">
                  {{ recording.analyses[type].resumo }}
                </p>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div
                    v-if="recording.analyses[type].pontos_positivos?.length"
                    class="flex flex-col gap-1.5"
                  >
                    <span class="text-xs font-medium text-n-teal-11">
                      {{
                        t(
                          'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.STRENGTHS'
                        )
                      }}
                    </span>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(point, index) in recording.analyses[type]
                          .pontos_positivos"
                        :key="index"
                        class="flex items-start gap-1.5 text-sm text-n-slate-12"
                      >
                        <span
                          class="mt-0.5 shrink-0 i-lucide-check-circle-2 size-3.5 text-n-teal-10"
                        />
                        <span>{{ point }}</span>
                      </li>
                    </ul>
                  </div>
                  <div
                    v-if="recording.analyses[type].pontos_atencao?.length"
                    class="flex flex-col gap-1.5"
                  >
                    <span class="text-xs font-medium text-n-amber-11">
                      {{
                        t(
                          'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.ATTENTION'
                        )
                      }}
                    </span>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(point, index) in recording.analyses[type]
                          .pontos_atencao"
                        :key="index"
                        class="flex items-start gap-1.5 text-sm text-n-slate-12"
                      >
                        <span
                          class="mt-0.5 shrink-0 i-lucide-alert-triangle size-3.5 text-n-amber-10"
                        />
                        <span>{{ point }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>

              <!-- Objeções -->
              <template v-else-if="type === 'objecoes'">
                <span
                  class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
                >
                  {{
                    t(
                      'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.OBJECOES'
                    )
                  }}
                </span>
                <p
                  v-if="recording.analyses.objecoes.resumo"
                  class="text-sm leading-6 text-n-slate-12"
                >
                  {{ recording.analyses.objecoes.resumo }}
                </p>
                <ul
                  v-if="recording.analyses.objecoes.objecoes?.length"
                  class="flex flex-col gap-2"
                >
                  <li
                    v-for="(item, index) in recording.analyses.objecoes
                      .objecoes"
                    :key="index"
                    class="flex flex-col gap-1 p-2.5 rounded-lg bg-n-alpha-2"
                  >
                    <div class="flex items-start gap-1.5">
                      <span
                        :class="
                          item.bem_tratada
                            ? 'i-lucide-check-circle-2 text-n-teal-10'
                            : 'i-lucide-x-circle text-n-ruby-10'
                        "
                        class="mt-0.5 shrink-0 size-3.5"
                      />
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ item.objecao }}
                      </span>
                    </div>
                    <p class="text-sm leading-6 pl-5 text-n-slate-11">
                      {{ item.tratamento }}
                    </p>
                  </li>
                </ul>
              </template>

              <!-- Próximos passos -->
              <template v-else-if="type === 'proximos'">
                <span
                  class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
                >
                  {{
                    t(
                      'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.TYPES.PROXIMOS'
                    )
                  }}
                </span>
                <p
                  v-if="recording.analyses.proximos.resumo"
                  class="text-sm leading-6 text-n-slate-12"
                >
                  {{ recording.analyses.proximos.resumo }}
                </p>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div
                    v-if="recording.analyses.proximos.proximos_passos?.length"
                    class="flex flex-col gap-1.5"
                  >
                    <span class="text-xs font-medium text-n-slate-11">
                      {{
                        t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ATA.NEXT_STEPS')
                      }}
                    </span>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(item, index) in recording.analyses.proximos
                          .proximos_passos"
                        :key="index"
                        class="flex items-start gap-1.5 text-sm text-n-slate-12"
                      >
                        <span
                          class="mt-0.5 shrink-0 i-lucide-arrow-right-circle size-3.5 text-n-slate-10"
                        />
                        <span>{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                  <div
                    v-if="recording.analyses.proximos.oportunidades?.length"
                    class="flex flex-col gap-1.5"
                  >
                    <span class="text-xs font-medium text-n-teal-11">
                      {{
                        t(
                          'CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.OPPORTUNITIES'
                        )
                      }}
                    </span>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(item, index) in recording.analyses.proximos
                          .oportunidades"
                        :key="index"
                        class="flex items-start gap-1.5 text-sm text-n-slate-12"
                      >
                        <span
                          class="mt-0.5 shrink-0 i-lucide-lightbulb size-3.5 text-n-teal-10"
                        />
                        <span>{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                  <div
                    v-if="recording.analyses.proximos.riscos?.length"
                    class="flex flex-col gap-1.5"
                  >
                    <span class="text-xs font-medium text-n-ruby-11">
                      {{
                        t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.ANALYSIS.RISKS')
                      }}
                    </span>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(item, index) in recording.analyses.proximos
                          .riscos"
                        :key="index"
                        class="flex items-start gap-1.5 text-sm text-n-slate-12"
                      >
                        <span
                          class="mt-0.5 shrink-0 i-lucide-alert-octagon size-3.5 text-n-ruby-10"
                        />
                        <span>{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </template>

        <!-- Transcript -->
        <div v-if="recording.transcript" class="w-full">
          <button
            type="button"
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="toggleTranscript(cardKey(recording))"
          >
            <span
              class="transition-transform i-lucide-chevron-down size-4"
              :class="{ 'rotate-180': expandedTranscripts[cardKey(recording)] }"
            />
            {{
              expandedTranscripts[cardKey(recording)]
                ? t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.TRANSCRIPT.HIDE')
                : t('CONTACTS_LAYOUT.SIDEBAR.RECORDINGS.TRANSCRIPT.SHOW')
            }}
          </button>
          <div
            v-if="expandedTranscripts[cardKey(recording)]"
            class="p-3 mt-2 overflow-y-auto text-sm leading-6 whitespace-pre-wrap rounded-lg max-h-72 text-n-slate-11 bg-n-alpha-1"
          >
            {{ recording.transcript }}
          </div>
        </div>
      </CardLayout>
    </template>
  </div>
</template>
